// [NEW] TicketDetailScreen Component - Tampilan tiket digital dengan QR code
import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import html2canvas from 'html2canvas';
import { HiOutlineCalendar, HiOutlineClock, HiOutlineLocationMarker, HiOutlineDownload, HiOutlineShare } from 'react-icons/hi';

const TicketDetailScreen = ({ ticket, onBack }) => {
    const [qrContent, setQrContent] = useState('');
    const [isDownloading, setIsDownloading] = useState(false);

    useEffect(() => {
        if (ticket) {
            // Generate QR content (will be enhanced with security hash later)
            const content = `TICKET:${ticket.eventId}:${ticket.id}:${generateHash(ticket)}`;
            setQrContent(content);
        }
    }, [ticket]);

    // Simplified hash generation (will be replaced with proper crypto later)
    const generateHash = (ticket) => {
        const str = `${ticket.id}${ticket.userId}${ticket.eventId}`;
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash;
        }
        return Math.abs(hash).toString(16).substring(0, 16);
    };

    const getStatusInfo = () => {
        const now = new Date();
        const eventDate = ticket.eventDate?.toDate ? ticket.eventDate.toDate() : new Date(ticket.eventDate);

        if (ticket.status === 'refunded') {
            return { icon: '🔄', text: 'Refunded', color: 'text-orange-600', bgColor: 'bg-orange-50' };
        }
        if (ticket.status === 'used' || ticket.checkedInAt) {
            return { icon: '✓', text: 'Sudah Masuk', color: 'text-gray-600', bgColor: 'bg-gray-50' };
        }
        if (eventDate < now) {
            return { icon: '❌', text: 'Expired', color: 'text-red-600', bgColor: 'bg-red-50' };
        }
        return { icon: '✅', text: 'Valid', color: 'text-green-600', bgColor: 'bg-green-50' };
    };

    const handleDownload = async () => {
        setIsDownloading(true);
        try {
            const element = document.getElementById('ticket-canvas');
            const canvas = await html2canvas(element, {
                scale: 2,
                backgroundColor: '#ffffff'
            });

            const link = document.createElement('a');
            link.download = `ticket-${ticket.id}.png`;
            link.href = canvas.toDataURL();
            link.click();
        } catch (error) {
            console.error('Download error:', error);
            alert('Gagal mengunduh tiket');
        } finally {
            setIsDownloading(false);
        }
    };

    const handleShare = async () => {
        const text = `🎫 Tiket: ${ticket.eventTitle}\n📅 ${ticket.eventDate?.toDate?.()?.toLocaleDateString('id-ID')}\n\nTiket ID: ${ticket.id}`;

        if (navigator.share) {
            try {
                await navigator.share({
                    title: 'Tiket Event Saya',
                    text: text
                });
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error('Share error:', error);
                }
            }
        } else {
            // Fallback: copy to clipboard
            navigator.clipboard.writeText(text);
            alert('Informasi tiket disalin ke clipboard');
        }
    };

    if (!ticket) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <p className="text-gray-500">Tiket tidak ditemukan</p>
            </div>
        );
    }

    const status = getStatusInfo();
    const isValid = status.text === 'Valid';

    return (
        <div className="min-h-screen bg-gradient-to-b from-green-50 to-gray-50 pb-24">
            {/* Header */}
            <div className="sticky top-0 bg-white z-10 px-4 py-3 border-b flex items-center">
                <button onClick={onBack} className="mr-4 p-2 hover:bg-gray-100 rounded-full">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>
                </button>
                <h2 className="font-semibold text-gray-800">Detail Tiket</h2>
            </div>

            {/* Ticket Card */}
            <div className="p-4">
                <div id="ticket-canvas" className="bg-white rounded-3xl shadow-2xl overflow-hidden border-2 border-green-100">
                    {/* Header Section */}
                    <div className="bg-gradient-to-r from-green-700 to-teal-600 p-6 text-white">
                        <div className="flex items-center mb-2">
                            <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center mr-3">
                                <span className="text-2xl">🎫</span>
                            </div>
                            <div>
                                <p className="text-xs opacity-90">E-Ticket</p>
                                <p className="font-bold">Semesta Event</p>
                            </div>
                        </div>
                    </div>

                    {/* Event Info */}
                    <div className="p-6">
                        <h1 className="text-2xl font-bold text-gray-900 mb-4">{ticket.eventTitle}</h1>

                        {ticket.categoryName && (
                            <div className="inline-block px-3 py-1.5 bg-teal-100 text-teal-800 rounded-full text-sm font-semibold mb-4">
                                {ticket.categoryName}
                            </div>
                        )}

                        <div className="space-y-3 mb-6">
                            <div className="flex items-start">
                                <HiOutlineCalendar className="w-5 h-5 text-green-700 mr-3 mt-0.5 flex-shrink-0" />
                                <div>
                                    <p className="text-sm text-gray-500">Tanggal</p>
                                    <p className="font-semibold text-gray-800">
                                        {ticket.eventDate?.toDate?.()?.toLocaleDateString('id-ID', {
                                            weekday: 'long',
                                            day: 'numeric',
                                            month: 'long',
                                            year: 'numeric'
                                        })}
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start">
                                <HiOutlineClock className="w-5 h-5 text-green-700 mr-3 mt-0.5 flex-shrink-0" />
                                <div>
                                    <p className="text-sm text-gray-500">Waktu</p>
                                    <p className="font-semibold text-gray-800">
                                        {ticket.eventDate?.toDate?.()?.toLocaleTimeString('id-ID', {
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })} WIB
                                    </p>
                                </div>
                            </div>

                            {ticket.eventLocation && (
                                <div className="flex items-start">
                                    <HiOutlineLocationMarker className="w-5 h-5 text-green-700 mr-3 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <p className="text-sm text-gray-500">Lokasi</p>
                                        <p className="font-semibold text-gray-800">{ticket.eventLocation}</p>
                                    </div>
                                </div>
                            )}

                            {/* [BARU] Info Jam Pembelian */}
                            {(ticket.purchaseTime || ticket.createdAt) && (
                                <div className="flex items-start">
                                    <HiOutlineClock className="w-5 h-5 text-gray-400 mr-3 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <p className="text-sm text-gray-500">Dibeli pada</p>
                                        <p className="font-medium text-gray-600">
                                            {ticket.purchaseTime || ticket.createdAt?.toDate?.()?.toLocaleString('id-ID')}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Divider */}
                        <div className="relative my-6">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t-2 border-dashed border-gray-300"></div>
                            </div>
                            <div className="absolute -left-8 top-1/2 -translate-y-1/2 w-6 h-6 bg-gray-50 rounded-full"></div>
                            <div className="absolute -right-8 top-1/2 -translate-y-1/2 w-6 h-6 bg-gray-50 rounded-full"></div>
                        </div>

                        {/* QR Code Section */}
                        <div className="text-center py-4">
                            <div className={`inline-block p-6 ${isValid ? 'bg-white' : 'bg-gray-100'} rounded-2xl border-2 ${isValid ? 'border-green-200' : 'border-gray-300'}`}>
                                {isValid ? (
                                    <QRCodeSVG
                                        value={qrContent}
                                        size={200}
                                        level="H"
                                        includeMargin={true}
                                    />
                                ) : (
                                    <div className="w-[200px] h-[200px] flex items-center justify-center text-gray-400">
                                        <div className="text-center">
                                            <p className="text-4xl mb-2">{status.icon}</p>
                                            <p className="text-sm font-medium">{status.text}</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <p className="text-xs text-gray-500 mt-3">
                                Tunjukkan QR code ini saat check-in
                            </p>
                        </div>

                        {/* Ticket Info */}
                        <div className="bg-gray-50 rounded-xl p-4 mt-6 space-y-2">
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-500">Ticket ID:</span>
                                <span className="font-mono text-gray-800 text-xs">{ticket.id}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-500">Atas nama:</span>
                                <span className="font-semibold text-gray-800">{ticket.userName || 'Guest'}</span>
                            </div>
                            {ticket.pricePaid > 0 && (
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-500">Harga:</span>
                                    <span className="font-semibold text-green-700">
                                        Rp {ticket.pricePaid.toLocaleString('id-ID')}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Status Badge */}
                        <div className={`mt-4 p-4 ${status.bgColor} rounded-xl text-center`}>
                            <p className={`text-lg font-bold ${status.color}`}>
                                {status.icon} {status.text}
                            </p>
                            {ticket.checkedInAt && (
                                <p className="text-xs text-gray-600 mt-1">
                                    Check-in: {ticket.checkedInAt.toDate?.()?.toLocaleString('id-ID')}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="bg-gray-50 px-6 py-4 border-t">
                        <p className="text-xs text-gray-500 text-center">
                            Tiket ini tidak dapat dipindahtangankan
                        </p>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="mt-6 grid grid-cols-2 gap-3">
                    <button
                        onClick={handleDownload}
                        disabled={isDownloading}
                        className="flex items-center justify-center gap-2 px-6 py-3 bg-white border-2 border-green-700 text-green-700 rounded-xl font-semibold hover:bg-green-50 transition disabled:opacity-50"
                    >
                        <HiOutlineDownload className="w-5 h-5" />
                        {isDownloading ? 'Downloading...' : 'Download'}
                    </button>

                    <button
                        onClick={handleShare}
                        className="flex items-center justify-center gap-2 px-6 py-3 bg-green-700 text-white rounded-xl font-semibold hover:bg-green-800 transition"
                    >
                        <HiOutlineShare className="w-5 h-5" />
                        Share
                    </button>
                </div>

                {/* Important Notes */}
                <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                    <p className="text-sm text-yellow-900 font-semibold mb-2">⚠️ Penting:</p>
                    <ul className="text-xs text-yellow-800 space-y-1 list-disc list-inside">
                        <li>Simpan tiket ini dengan baik</li>
                        <li>Datang 15 menit sebelum acara dimulai</li>
                        <li>Pastikan QR code dapat dipindai dengan jelas</li>
                        {isValid && <li>Screenshot QR code untuk backup</li>}
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default TicketDetailScreen;
